import { useCallback } from 'react';
import { toast } from 'sonner';
import type { Workflow, WorkflowCreationData } from '@/components/ApprovalStageWizard/types';
import type { CreateWorkflowRequest } from '@/types/workflow';
import * as workflowService from '@/services/workflowService';
import { getTemplateStages, mapApproverTypeToBackend, mapBackendWorkflowToFrontend, mapRoleTypeToBackend } from '../utils';

export interface UseWorkflowCreationParams {
  isLive: boolean;
  workflows: Workflow[];
  setWorkflows: React.Dispatch<React.SetStateAction<Workflow[]>>;
  loadWorkflows: () => Promise<void>;
  /** Called with the new workflow id once creation succeeds. */
  onWorkflowCreated: (workflowId: string) => void;
  /** Called once a duplication attempt finishes successfully. */
  onWorkflowDuplicated: () => void;
}

export interface UseWorkflowCreationResult {
  handleCreateWorkflow: (data: WorkflowCreationData, selectedTemplate: string | null) => Promise<void>;
  handleDuplicate: (
    workflowId: string,
    newName: string,
    options: { copyStages: boolean; copyRules: boolean; copyAssignments: boolean }
  ) => Promise<void>;
}

/**
 * Owns creating a workflow (optionally from a quick-start template) and
 * duplicating an existing workflow.
 */
export const useWorkflowCreation = ({
  isLive,
  workflows,
  setWorkflows,
  loadWorkflows,
  onWorkflowCreated,
  onWorkflowDuplicated,
}: UseWorkflowCreationParams): UseWorkflowCreationResult => {
  const handleCreateWorkflow = useCallback(async (
    data: WorkflowCreationData,
    selectedTemplate: string | null
  ) => {
    const now = new Date();

    if (isLive) {
      try {
        // Get stages from template if one was selected
        const templateStages = selectedTemplate ? getTemplateStages(selectedTemplate) : [];

        // Convert template stages to API format, or use default stage if no template
        const apiStages: CreateWorkflowRequest['stages'] = templateStages.length > 0
          ? templateStages.map((stage, index) => {
              const stageData = stage.stageData;

              // Map approver type from frontend to backend format
              const approverType = stageData?.approver
                ? mapApproverTypeToBackend(stageData.approver.approverType)
                : 'manager' as const;

              // Build approver object
              const approverObj: CreateWorkflowRequest['stages'][0]['approvers'][0] = {
                approverType,
                approverOrder: 1,
                cannotApproveOwn: stageData?.approver?.cannotApproveOwn ?? true,
              };

              // Add approver role if applicable
              if (stageData?.approver?.roleType) {
                approverObj.approverRole = mapRoleTypeToBackend(stageData.approver.roleType);
              }

              // Add specific user ID if applicable
              if (stageData?.approver?.specificUserId) {
                approverObj.approverId = String(stageData.approver.specificUserId);
              }

              // Build advanced config JSON from stageData
              const advancedConfig = stageData ? {
                smartRules: {
                  rules: stageData.smartRules.rules,
                  onApproved: stageData.smartRules.onApproved,
                  onRejected: stageData.smartRules.onRejected,
                  timeout: stageData.smartRules.timeout,
                },
                notifications: stageData.notifications,
              } : undefined;

              return {
                name: stage.name,
                description: stage.description,
                stageOrder: index + 1,
                approvalType: 'any' as const,
                requiredApprovals: 1,
                allowApproverEdit: stageData?.allowApproverEdit ?? false,
                advancedConfigJson: advancedConfig ? JSON.stringify(advancedConfig) : undefined,
                approvers: [approverObj],
              };
            })
          : [{
              name: 'Initial Stage',
              description: 'Default approval stage',
              stageOrder: 1,
              approvalType: 'any' as const,
              requiredApprovals: 1,
              approvers: [{
                approverType: 'manager' as const,
                approverOrder: 1,
              }],
            }];

        // Create workflow via API
        const createRequest: CreateWorkflowRequest = {
          name: data.name,
          description: data.description,
          isActive: false, // Start as inactive (draft)
          isDefault: false,
          stages: apiStages,
        };

        const createdWorkflow = await workflowService.createWorkflow(createRequest);
        const frontendWorkflow = mapBackendWorkflowToFrontend(createdWorkflow);

        setWorkflows([...workflows, frontendWorkflow]);
        onWorkflowCreated(frontendWorkflow.id);

        const stageMessage = templateStages.length > 0
          ? `with ${templateStages.length} pre-configured stage${templateStages.length > 1 ? 's' : ''}`
          : 'Add stages to get started';
        toast.success(`Workflow "${data.name}" created successfully ${stageMessage}!`);
      } catch (err) {
        console.error('Failed to create workflow:', err);
        toast.error('Failed to create workflow. Please try again.');
      }
    } else {
      // Demo mode - create locally with template support
      const workflowId = `wf-${Date.now()}`;

      // Get stages from template if one was selected
      const stages = selectedTemplate ? getTemplateStages(selectedTemplate) : [];

      const newWorkflow: Workflow = {
        id: workflowId,
        name: data.name,
        description: data.description,
        status: 'draft',
        stages: stages,
        createdAt: now,
        updatedAt: now,
        createdBy: 'current-user',
        assignedToCount: 0,
      };

      setWorkflows([...workflows, newWorkflow]);
      onWorkflowCreated(workflowId);

      const stageMessage = stages.length > 0
        ? `with ${stages.length} pre-configured stage${stages.length > 1 ? 's' : ''}`
        : 'Add stages to get started';
      toast.success(`Workflow "${data.name}" created successfully ${stageMessage}!`);
    }
  }, [isLive, workflows, setWorkflows, onWorkflowCreated]);

  const handleDuplicate = useCallback(async (
    workflowId: string,
    newName: string,
    options: {
      copyStages: boolean;
      copyRules: boolean;
      copyAssignments: boolean;
    }
  ) => {
    const sourceWorkflow = workflows.find((w) => w.id === workflowId);
    if (!sourceWorkflow) {
      toast.error('Source workflow not found');
      throw new Error('Source workflow not found');
    }

    if (isLive) {
      // In live mode, call backend API to duplicate
      await workflowService.duplicateWorkflow(
        workflowId,
        newName,
        options
      );

      // Refresh the full workflow list from the backend to ensure consistency
      await loadWorkflows();

      toast.success(`Workflow "${newName}" created successfully!`);
    } else {
      // Demo mode - create a copy locally
      const newWorkflow: Workflow = {
        id: `workflow-${Date.now()}`,
        name: newName,
        description: sourceWorkflow.description,
        status: 'draft', // Always create as draft
        stages: options.copyStages
          ? sourceWorkflow.stages.map((stage, index) => ({
              ...stage,
              id: `stage-${Date.now()}-${index}`,
              // Copy smart rules if option is enabled
              stageData: options.copyRules && stage.stageData
                ? { ...stage.stageData }
                : stage.stageData
                ? {
                    ...stage.stageData,
                    smartRules: { rules: [], onApproved: { type: 'continue' }, onRejected: { type: 'reject' } }, // Reset rules if not copying
                  }
                : undefined,
            }))
          : [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'system',
        assignedToCount: options.copyAssignments ? sourceWorkflow.assignedToCount : 0,
      };

      setWorkflows((prev) => [...prev, newWorkflow]);
      toast.success(`Workflow "${newName}" created successfully!`);
    }

    // Close the dialog
    onWorkflowDuplicated();
  }, [isLive, workflows, setWorkflows, loadWorkflows, onWorkflowDuplicated]);

  return { handleCreateWorkflow, handleDuplicate };
};
